import { useScroll } from 'framer-motion';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { useRef } from 'react';
import type { Thing } from 'schema-dts';
import { Day } from '@/entities/day';
import { Direction } from '@/entities/direction';
import { Place } from '@/entities/place';
import { getCommonPageProps } from '@/shared/api';
import { getDays } from '@/shared/api/days';
import { getDirections } from '@/shared/api/directions';
import { getPageDetail } from '@/shared/api/page-detail';
import { getPlaces } from '@/shared/api/places';
import { AppProgramResponse, getProgram } from '@/shared/api/program';
import { getSiteSettings } from '@/shared/api/site-settings';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import CtaSection, { CtaSectionProps } from '@/shared/components/sections/CtaSection';
import DepthSliderSection, { DepthSliderSectionProps } from '@/shared/components/sections/DepthSliderSection';
import IconTextSection, { IconTextSectionProps } from '@/shared/components/sections/IconTextSection';
import LiveGradientSection, { LiveGradientSectionProps } from '@/shared/components/sections/LiveGradientSection';
import LiveProgramSection, { LiveProgramSectionProps } from '@/shared/components/sections/LiveProgramSection';
import { transformLiveProgramFromApi } from '@/shared/components/sections/LiveProgramSection/transform-live-program';
import RhythmSection, { RhythmSectionProps } from '@/shared/components/sections/RhythmSection';
import GradientBG from '@/shared/components/shared/GradientBG';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import { CommonPageProps, ImageShape } from '@/shared/lib/types';
import { generateWebPageJsonLd } from '@/shared/lib/utils/page-json-ld';

const TNF_LIVE_DIRECTION_SLUGS = ['tnf-live', 'tnf-life'];

const EMPTY_PROGRAM_RESPONSE: AppProgramResponse = {
    status: 'error',
    data: { items: [], totalCount: 0 },
};

const LivePage = ({
    bodyClass,
    meta,
    breadcrumbs,
    liveGradientSectionProps,
    iconTextSectionProps,
    depthSliderSectionProps,
    rhythmSectionProps,
    liveProgramSectionProps,
    ctaSectionProps,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
    const ref = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });

    return (
        <DefaultLayout bodyClass={bodyClass} ref={ref}>
            <GradientBG scrollYProgress={scrollYProgress} />
            {liveGradientSectionProps.slogan && (
                <LiveGradientSection liveGradientSectionProps={liveGradientSectionProps} />
            )}
            {iconTextSectionProps.text && <IconTextSection iconTextSectionProps={iconTextSectionProps} />}
            {depthSliderSectionProps.slides.length > 0 && (
                <DepthSliderSection depthSliderSectionProps={depthSliderSectionProps} />
            )}
            {liveProgramSectionProps.items.length > 0 ? (
                <LiveProgramSection liveProgramSectionProps={liveProgramSectionProps} />
            ) : (
                rhythmSectionProps.events.length > 0 && <RhythmSection rhythmSectionProps={rhythmSectionProps} />
            )}
            {ctaSectionProps.title && <CtaSection ctaSectionProps={ctaSectionProps} />}
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-tnf-live"
                data={generateWebPageJsonLd({
                    name: liveGradientSectionProps.slogan || meta.title || '',
                    description: iconTextSectionProps.text || meta.description,
                    pagePath: '/events/tnf-live/',
                    breadcrumbs,
                })}
            />
        </DefaultLayout>
    );
};

export default LivePage;

type LivePageProps = CommonPageProps & {
    liveGradientSectionProps: LiveGradientSectionProps;
    rhythmSectionProps: RhythmSectionProps;
    liveProgramSectionProps: LiveProgramSectionProps;
    ctaSectionProps: CtaSectionProps;
    iconTextSectionProps: IconTextSectionProps;
    depthSliderSectionProps: DepthSliderSectionProps;
};

export const getStaticProps: GetStaticProps<LivePageProps> = async () => {
    const [pageResponse, commonPageProps, siteSettings, directionsResponse, placesResponse] = await Promise.all([
        getPageDetail({ params: { slug: 'live' } }).catch(() => ({
            status: 'error' as const,
        })),
        getCommonPageProps(),
        getSiteSettings({
            params: {
                keys: [
                    'live_first_screen_description',
                    'live_first_screen_caption',
                    'live_first_screen_link_text',
                    'live_first_screen_link',
                    'live_first_screen_video',

                    'live_icon_block_description',

                    'live_slider_slides',

                    'live_schedule_title',
                    'live_schedule_description',
                    'live_schedule_items',

                    'live_last_screen_title',
                    'live_last_screen_description',
                    'live_last_screen_button_text',
                    'live_last_screen_button_link',
                    'live_last_screen_caption',
                ],
            },
        }),
        getDirections().catch((): { status: 'error'; data: Direction[] } => ({
            status: 'error',
            data: [],
        })),
        getPlaces().catch((): { status: 'error'; data: Place[] } => ({
            status: 'error',
            data: [],
        })),
    ]);

    const pageId = pageResponse.status === 'success' ? (pageResponse.data?.id ?? null) : null;
    const daysResponse =
        pageId !== null
            ? await getDays({ params: { page: pageId } }).catch((): { status: 'error'; data: Day[] } => ({
                  status: 'error',
                  data: [],
              }))
            : { status: 'error' as const, data: [] as Day[] };
    const dayIds = (daysResponse.data ?? []).map((day) => day.id);

    const tnfLiveDirectionId =
        directionsResponse.data?.find((direction) => TNF_LIVE_DIRECTION_SLUGS.includes(direction.slug))?.id ?? null;

    const programsResponse =
        tnfLiveDirectionId !== null && dayIds.length > 0
            ? await getProgram({
                  params: { directions: [tnfLiveDirectionId], days: dayIds },
              }).catch(() => EMPTY_PROGRAM_RESPONSE)
            : EMPTY_PROGRAM_RESPONSE;

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'live-page',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.status === 'success' ? (pageResponse.data?.meta ?? []) : []),
            },
            liveGradientSectionProps: {
                slogan: siteSettings.data?.live_first_screen_description ?? '',
                location: siteSettings.data?.live_first_screen_caption ?? '',
                link: {
                    name: siteSettings.data?.live_first_screen_link_text ?? '',
                    href: siteSettings.data?.live_first_screen_link ?? '',
                },
                shortVideo: [
                    {
                        src: siteSettings.data?.live_first_screen_video?.[0]?.src ?? '',
                        type: 'video/mp4',
                    },
                ],
                longVideo: [
                    {
                        src: siteSettings.data?.live_first_screen_video?.[0]?.src ?? '',
                        type: 'video/mp4',
                    },
                ],
            },
            iconTextSectionProps: {
                text: siteSettings.data?.live_icon_block_description ?? '',
            },
            depthSliderSectionProps: {
                background: {
                    src: '/img/live/depth-slider/bg-1.svg',
                    alt: '',
                    width: 1920,
                    height: 1080,
                    priority: true,
                },
                slides: (siteSettings.data?.live_slider_slides ?? [])
                    .filter((slide): slide is typeof slide & { image: ImageShape } => Boolean(slide.image?.src))
                    .map((slide, index) => ({
                        id: `slide-${index}`,
                        label: slide.title,
                        category: 'TNF LIVE',
                        title: slide.title,
                        description: slide.description,
                        image: {
                            src: slide.image.src,
                            alt: slide.image.alt ?? '',
                            width: slide.image.width ?? 1024,
                            height: slide.image.height ?? 682,
                        },
                    })),
            },
            rhythmSectionProps: {
                title: siteSettings.data?.live_schedule_title ?? '',
                description: siteSettings.data?.live_schedule_description ?? '',
                events: (siteSettings.data?.live_schedule_items ?? [])
                    .filter((item): item is typeof item & { image: ImageShape } => Boolean(item.image?.src))
                    .map((item) => ({
                        time: item.time,
                        title: item.title,
                        description: item.description,
                        image: {
                            src: item.image.src,
                            alt: item.image.alt ?? '',
                            width: item.image.width ?? 151,
                            height: item.image.height ?? 195,
                        },
                    })),
            },
            liveProgramSectionProps: {
                title: siteSettings.data?.live_schedule_title ?? '',
                description: siteSettings.data?.live_schedule_description ?? '',
                items: transformLiveProgramFromApi(
                    programsResponse.data?.items ?? [],
                    daysResponse.data ?? [],
                    placesResponse.data ?? [],
                ),
            },
            ctaSectionProps: {
                title: siteSettings.data?.live_last_screen_title ?? '',
                subtitle: siteSettings.data?.live_last_screen_description ?? '',
                note: siteSettings.data?.live_last_screen_caption ?? '',
                link: {
                    name: siteSettings.data?.live_last_screen_button_text ?? '',
                    href: siteSettings.data?.live_last_screen_button_link ?? '',
                },
            },
        } satisfies LivePageProps,
        revalidate: 60,
    };
};
